import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faUserGraduate,
  faUsersGear,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import swal from "sweetalert";
import { APISD } from "../../../../../config/apisd";
import { APIMS } from "../../../../../config/apims";
import ModalUpdate from "./modal/update";
import "bulma/css/bulma.css";
import "../../../../../index.css";

const API_LIMIT = 1000;
const text = (value) => (value == null ? "" : String(value));

const getInitials = (name) => {
  const words = text(name).trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) return "-";

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

export default function WaliKelas() {
  document.title = "Wali Kelas";
  const token = localStorage.getItem("token");
  const userLevel = localStorage.getItem("level");
  const userBranch = localStorage.getItem("ysb_branch_id");
  const location = useLocation();
  const navigate = useNavigate();
  const headers = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token],
  );

  const [permission, setPermission] = useState({ create: 0, update: 0 });

  const [loading, setLoading] = useState(true);
  const [loadingSchool, setLoadingSchool] = useState(false);

  const [isFetching, setIsFetching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [filterDirty, setFilterDirty] = useState(false);

  const [modalUpdate, setModalUpdate] = useState(false);

  const [selectedData, setSelectedData] = useState(null);

  const [semesters, setSemesters] = useState([]);
  const [branches, setBranches] = useState([]);
  const [schools, setSchools] = useState([]);

  const [rowsBySemester, setRowsBySemester] = useState({});

  const [academicYear, setAcademicYear] = useState("");
  const [semesterName, setSemesterName] = useState("");

  const [search, setSearch] = useState("");

  const [filters, setFilters] = useState({
    ysb_semester_id: "",
    ysb_branch_id: "",
    ysb_school_id: "",
  });

  useEffect(() => {
    if (["developer", "superadmin"].includes(userLevel)) {
      setPermission({ create: 1, update: 1 });
      return;
    }
    const access = (
      JSON.parse(localStorage.getItem("role_permission")) || []
    ).find((item) => item.menu?.url === location.pathname);
    if (!access) navigate("/dashboard");
    else setPermission({ create: access.create, update: access.update });
  }, [location.pathname, navigate, userLevel]);

  const yearOptions = useMemo(
    () => [
      ...new Set(semesters.map((item) => item?.name_year).filter(Boolean)),
    ],
    [semesters],
  );
  const semesterOptions = useMemo(
    () =>
      semesters
        .filter((item) => item?.name_year === academicYear)
        .sort((a, b) => Number(a?.semester) - Number(b?.semester)),
    [academicYear, semesters],
  );

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      try {
        const [branchRes, semesterRes] = await Promise.all([
          APIMS.get(
            `/api/access/branches?page=1&limit=${API_LIMIT}&ascending=1&search=&branch=${userBranch}&level=${userLevel}`,
            headers,
          ),
          APIMS.get(
            `/api/access/semesters?page=1&limit=${API_LIMIT}&ascending=0&search=`,
            headers,
          ),
        ]);
        const branchData = branchRes?.data?.data || [];
        const semesterData = semesterRes?.data?.data || [];
        const active =
          semesterData.find((item) => Number(item?.status) === 1) ||
          semesterData[0];
        setBranches(branchData);
        setSemesters(semesterData);
        setAcademicYear(active?.name_year || "");
        setSemesterName(active?.semester || "");
        setFilters((old) => ({
          ...old,
          ysb_branch_id: branchData[0]?.branch_code || "",
          ysb_semester_id: active?.id || "",
        }));
      } catch (error) {
        swal({
          title: "Gagal",
          text:
            error?.response?.data?.message || "Gagal memuat filter wali kelas",
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    loadOptions();
  }, [headers, userBranch, userLevel]);

  useEffect(() => {
    if (!filters.ysb_branch_id) return;
    const loadSchools = async () => {
      setLoadingSchool(true);
      try {
        const response = await APIMS.get(
          `/api/access/schools?page=1&limit=${API_LIMIT}&ascending=1&branch=${filters.ysb_branch_id}&level=${userLevel}`,
          headers,
        );
        let data = response?.data?.data || [];
        if (["developer", "superadmin"].includes(userLevel))
          data = data.filter(
            (item) => item?.ysb_branch_id === filters.ysb_branch_id,
          );
        if (filters.ysb_branch_id === "BKS")
          data = data.filter((item) => item?.school_code !== "BASEMENT_BEKASI");
        data.sort((a, b) =>
          text(a?.school_name).localeCompare(text(b?.school_name), "id", {
            numeric: true,
          }),
        );
        setSchools(data);
        setFilters((old) => ({
          ...old,
          ysb_school_id: data[0]?.school_code || "",
        }));
      } catch {
        setSchools([]);
      } finally {
        setLoadingSchool(false);
      }
    };
    loadSchools();
  }, [filters.ysb_branch_id, headers, userLevel]);

  const fetchWalas = useCallback(
    async () => {
      const semesterIds = semesterOptions
        .map((semester) => semester?.id)
        .filter(Boolean);

      if (
        semesterIds.length === 0 ||
        !filters.ysb_branch_id ||
        !filters.ysb_school_id
      ) {
        return;
      }

      setIsFetching(true);
      try {
        const responses = await Promise.all(
          semesterIds.map((semesterId) =>
            APISD.post(
              "/api/walas-assignments",
              {
                page: 1,
                limit: API_LIMIT,
                ascending: true,
                search: "",
                ysb_academic_year_id: academicYear,
                ysb_semester_id: semesterId,
                ysb_branch_id: filters.ysb_branch_id,
                ysb_school_id: filters.ysb_school_id,
              },
              headers,
            ),
          ),
        );

        const nextRowsBySemester = semesterIds.reduce(
          (result, semesterId, index) => ({
            ...result,
            [String(semesterId)]: Array.isArray(
              responses[index]?.data?.data,
            )
              ? responses[index].data.data
              : [],
          }),
          {},
        );

        setRowsBySemester(nextRowsBySemester);
        setHasSearched(true);
        setFilterDirty(false);
      } catch (error) {
        setRowsBySemester({});
        setHasSearched(true);
        swal({
          title: "Gagal",
          text:
            error?.response?.data?.message || "Gagal mengambil data wali kelas",
          icon: "error",
        });
      } finally {
        setIsFetching(false);
      }
    },
    [academicYear, filters, headers, semesterOptions],
  );

  useEffect(() => {
    if (
      !loading &&
      !loadingSchool &&
      !hasSearched &&
      !filterDirty &&
      filters.ysb_school_id
    ) {
      fetchWalas();
    }
  }, [
    fetchWalas,
    filterDirty,
    filters.ysb_school_id,
    hasSearched,
    loading,
    loadingSchool,
  ]);

  const changeFilter = (event) => {
    const { name, value } = event.target;
    setFilters((old) => ({
      ...old,
      [name]: value,
      ...(name === "ysb_branch_id" ? { ysb_school_id: "" } : {}),
    }));
    setFilterDirty(true);
    setHasSearched(false);
  };

  const changeYear = (event) => {
    const year = event.target.value;
    const options = semesters
      .filter((item) => item?.name_year === year)
      .sort((a, b) => Number(a?.semester) - Number(b?.semester));
    const selected =
      options.find((item) => Number(item?.status) === 1) || options[0];
    setAcademicYear(year);
    setSemesterName(selected?.semester || "");
    setFilters((old) => ({ ...old, ysb_semester_id: selected?.id || "" }));
    setFilterDirty(true);
    setHasSearched(false);
  };

  const switchSemester = (semester) => {
    if (!semester?.id || semester?.id === filters.ysb_semester_id) return;
    setSemesterName(semester?.semester || "");
    setFilters((old) => ({ ...old, ysb_semester_id: semester.id }));
    setSearch("");
  };

  const rows = useMemo(
    () => rowsBySemester[String(filters.ysb_semester_id)] || [],
    [filters.ysb_semester_id, rowsBySemester],
  );

  const displayedRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return rows
      .filter(
        (item) =>
          !keyword ||
          [
            item?.name_level,
            item?.name_kelas,
            ...(item?.teachers || []).map((teacher) => teacher?.full_name),
          ].some((value) => text(value).toLowerCase().includes(keyword)),
      )
      .sort(
        (a, b) =>
          text(a?.name_level).localeCompare(text(b?.name_level), "id", {
            numeric: true,
          }) ||
          text(a?.name_kelas).localeCompare(text(b?.name_kelas), "id", {
            numeric: true,
          }),
      );
  }, [rows, search]);

  const displayedRowsWithLevelSpan = useMemo(
    () =>
      displayedRows.map((item, index, currentRows) => {
        const currentLevel = text(item?.name_level).trim();
        const previousLevel =
          index > 0 ? text(currentRows[index - 1]?.name_level).trim() : null;

        if (index > 0 && currentLevel === previousLevel) {
          return { ...item, levelRowSpan: 0 };
        }

        let levelRowSpan = 1;

        for (let nextIndex = index + 1; nextIndex < currentRows.length; nextIndex += 1) {
          const nextLevel = text(currentRows[nextIndex]?.name_level).trim();

          if (nextLevel !== currentLevel) break;
          levelRowSpan += 1;
        }

        return { ...item, levelRowSpan };
      }),
    [displayedRows],
  );
  const complete =
    !!filters.ysb_semester_id &&
    !!filters.ysb_branch_id &&
    !!filters.ysb_school_id &&
    !loadingSchool;
  const context = {
    academicYear,
    semesterList: semesterOptions,
    branchId: filters.ysb_branch_id,
    branchName: branches.find(
      (item) => item?.branch_code === filters.ysb_branch_id,
    )?.branch_name,
    schoolId: filters.ysb_school_id,
    schoolName: schools.find(
      (item) => item?.school_code === filters.ysb_school_id,
    )?.school_name,
    classes: rows,
  };

  const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

  const toTitleCase = (value) => {
    const s = safeStr(value).toLowerCase().trim();
    if (!s) return "";
    return s.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));
  };

  return (
    <div className="body sims-page">
      {modalUpdate && selectedData && (
        <ModalUpdate
          show
          context={context}
          dataUpdate={selectedData}
          GetResponseData={() => fetchWalas()}
          onHide={() => {
            setModalUpdate(false);
            setSelectedData(null);
          }}
        />
      )}

      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faUserGraduate} />
            </span>
            <h4 className="page-title-text">Wali Kelas</h4>
          </div>
        </div>
      </div>

      <div className="body-content sims-content">
        {loading ? (
          <div className="sims-filter-card sims-filter-card-four-shimmer">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="shimmer sims-filter-skeleton" />
            ))}
          </div>
        ) : (
          <div className="sims-filter-card sims-filter-card-four">
            <div className="sims-filter-field">
              <label className="sims-filter-label">Tahun Ajaran</label>
              <select
                className="sims-filter-select"
                value={academicYear}
                onChange={changeYear}
              >
                <option value="" disabled>
                  Pilih Tahun Ajaran
                </option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div className="sims-filter-field">
              <label className="sims-filter-label">Cabang</label>
              <select
                className="sims-filter-select"
                name="ysb_branch_id"
                value={filters.ysb_branch_id}
                onChange={changeFilter}
              >
                <option value="" disabled>
                  Pilih Cabang
                </option>
                {branches.map((item) => (
                  <option key={item?.branch_code} value={item?.branch_code}>
                    {item?.branch_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="sims-filter-field">
              <label className="sims-filter-label">Sekolah</label>
              <select
                className="sims-filter-select"
                name="ysb_school_id"
                value={filters.ysb_school_id}
                onChange={changeFilter}
                disabled={loadingSchool || !filters.ysb_branch_id}
              >
                <option value="" disabled>
                  {loadingSchool ? "Memuat data sekolah..." : "Pilih Sekolah"}
                </option>
                {schools.map((item) => (
                  <option key={item?.school_code} value={item?.school_code}>
                    {item?.school_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="sims-filter-action">
                <button
                  type="button"
                  className="sims-filter-button"
                  disabled={!complete || !filterDirty}
                  onClick={() => fetchWalas()}
                >
                  Tampilkan Data
                </button>
              </div>

            
            {/* <div className="filter-actions-two">
              <div className="sims-filter-action filter-actions-two-field">
                <button
                  type="button"
                  className="sims-filter-button"
                  disabled={!complete || !filterDirty}
                  onClick={() => fetchWalas()}
                >
                  Tampilkan Data
                </button>
              </div>
              {!!permission.create && (
                <div className="sims-filter-action filter-actions-two-field">
                  <button
                    type="button"
                    className="sims-filter-button btn-create"
                    disabled={!complete}
                    onClick={() => setModalAdd(true)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Tambah Wali Kelas</span>
                  </button>
                </div>
              )}
            </div> */}
          </div>
        )}

        {isFetching && (
          <div className="sims-loading">
            <PulseLoader color="#3f5bea" size={12} />
          </div>
        )}

        {hasSearched && !isFetching && (
          <>
            <div className="sims-search-wrap mb-3">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="sims-search-icon"
              />
              <input
                type="text"
                className="sims-search-input"
                placeholder="Cari kelas atau wali kelas"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <div className="sims-table-tab-card ">
              <div className="sims-table-tab-head">
                <div className="sims-table-tab-title">
                  <h5>Data Wali Kelas - Semester {semesterName}</h5>
                </div>
                <div className="sims-table-tab-semester-switch">
                  {semesterOptions.map((semester) => (
                    <button
                      type="button"
                      key={semester?.id}
                      className={`sims-table-tab-semester ${String(filters.ysb_semester_id) === String(semester?.id) ? "is-active" : ""}`}
                      onClick={() => switchSemester(semester)}
                      disabled={isFetching}
                    >
                      Semester {semester?.semester}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="sims-table-tab-wrap">
                <table className="sims-table-tab">
                  <thead>
                    <tr>
                      {/* <th className="sims-col-number">No</th> */}
                      <th>Tingkatan</th>
                      <th>Kelas</th>
                      <th>Wali Kelas</th>
                      <th className="sims-col-action">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="sims-empty-state">
                          <div className="sims-empty-state-content">
                            Data wali kelas tidak ditemukan
                          </div>
                        </td>
                      </tr>
                    ) : (
                      displayedRowsWithLevelSpan.map((item, index) => (
                        <tr
                          key={`${item?.ysb_kelas_id}-${item?.ysb_semester_id}`}
                        >
                          {/* <td className="sims-col-number">
                            <span className="sims-number-badge">
                              {index + 1}
                            </span>
                          </td> */}
                          {item.levelRowSpan > 0 && (
                            <td
                              rowSpan={item.levelRowSpan}
                              className="text-center walas-level-cell"
                            >
                              <span className="sims-text-bold sims-number-badge" style={{fontSize: "14px"}}>
                                {item?.name_level || "-"}
                              </span>
                            </td>
                          )}
                          <td style={{fontSize: "13px"}}>{toTitleCase(item?.name_kelas) || "-"}</td>
                          <td>
                            {(item?.teachers || []).length > 0 ? (
                              <div className="walas-name-list">
                                {item.teachers.map((teacher, teacherIndex) => (
                                  <span
                                    className="walas-name-chip"
                                    key={
                                      teacher?.assignment_id ||
                                      teacher?.ysb_teacher_id ||
                                      `${teacher?.full_name}-${teacherIndex}`
                                    }
                                    title={teacher?.full_name || "Wali kelas"}
                                  >
                                    <span className="walas-name-initial">
                                      {getInitials(teacher?.full_name)}
                                    </span>

                                    <span className="walas-name-text">
                                      {teacher?.full_name || "-"}
                                    </span>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="walas-name-empty">-</span>
                            )}
                          </td>
                          {item.levelRowSpan > 0 && (
                            <td
                              rowSpan={item.levelRowSpan}
                              className="sims-col-action walas-level-action-cell"
                            >
                              {!!permission.update && (
                                <button
                                  type="button"
                                  className="sims-action-button config-matpel-table-button config-matpel-table-button-secondary walas-manage-button"
                                  title={`Kelola wali kelas tingkatan ${item?.name_level || ""}`}
                                  onClick={() => {
                                    setSelectedData({
                                      name_level: item?.name_level,
                                      classes: displayedRows.filter(
                                        (row) =>
                                          String(row?.name_level) ===
                                          String(item?.name_level),
                                      ),
                                    });
                                    setModalUpdate(true);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faUsersGear} />
                                  <span>Kelola Walas</span>
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
      <style>{`
        .walas-level-cell {
          vertical-align: middle !important;
          // background-color: #f8fafffd !important;
          background: #fbfcff !important;
          color: #3156a3;
          border : 1px solid #e7f2ff;
        }

        .walas-level-action-cell {
          vertical-align: middle !important;
          text-align: center !important;
          // background: #fbfcff !important;
          border: 1px solid #e7f2ff !important;
        }

        .walas-manage-button {
          width: auto !important;
          min-width: 132px;
          padding: 0 14px !important;
          gap: 7px;
          white-space: nowrap;
        }

        .walas-name-list {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
          min-width: 190px;
        }

        .walas-name-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          max-width: 230px;
          min-height: 30px;
          padding: 6px 12px 6px 7px;
          border: 1px solid #d7e2f7;
          border-radius: 12px;
          background: #f8faff;
          color: #172554;
          font-size: 12px;
          font-weight: 700;
          line-height: 1.2;
          white-space: nowrap;
        }

        .walas-name-initial {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 28px;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #dce8ff;
          color: #3f5fca;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: -0.2px;
        }

        .walas-name-text {
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .walas-name-empty {
          color: #94a3b8;
        }

        @media (max-width: 768px) {
          .walas-name-list {
            min-width: 160px;
          }

          .walas-name-chip {
            max-width: 190px;
          }
        }
      `}</style>
    </div>
  );
}
